/**
 * Section Definitions for Wizard Navigation
 * 
 * Defines metadata for all 17 sections across 5 tabs to enable
 * section-based navigation in the wizard interface.
 * 
 * Environment tab reorganization: Business Travel, Employee Commuting,
 * Accommodation, Company Expenses, and Freight & Logistics are now
 * nested under Carbon Emissions as Scope 3 sub-categories.
 */

import {
  Building2,
  Leaf,
  TrendingUp,
  Cloud,
  AlertTriangle, 
  Shield, 
  Users, 
  Users2, 
  Eye, 
  AlertOctagon, 
  FileCheck,
  type LucideIcon
} from "lucide-react";

export interface WizardSection {
  id: string;
  tab: "general" | "environment" | "social" | "governance" | "sustainability-story";
  cardKey: string; // Used to match with expandedMetric state
  displayName: string;
  order: number; // Order within the tab (0-indexed)
  icon: LucideIcon; // Icon component for the section
}

export const WIZARD_SECTIONS: WizardSection[] = [
  // GENERAL TAB (3 sections)
  {
    id: "general-info",
    tab: "general",
    cardKey: "generalInfo",
    displayName: "General Information",
    order: 0,
    icon: Building2,
  },
  {
    id: "sustainability-practices",
    tab: "general",
    cardKey: "sustainability",
    displayName: "Policies & targets",
    order: 1,
    icon: Leaf,
  },
  // ENVIRONMENT TAB (6 sections)
  {
    id: "carbon-emissions",
    tab: "environment",
    cardKey: "carbonEmissions",
    displayName: "Carbon Emissions",
    order: 0,
    icon: Cloud,
  },
  {
    id: "pollution",
    tab: "environment",
    cardKey: "pollution",
    displayName: "Pollution",
    order: 1,
    icon: AlertTriangle,
  },
  {
    id: "biodiversity",
    tab: "environment",
    cardKey: "biodiversity",
    displayName: "Biodiversity",
    order: 2,
    icon: Leaf,
  },
  {
    id: "water",
    tab: "environment",
    cardKey: "water",
    displayName: "Water",
    order: 3,
    icon: TrendingUp,
  },
  {
    id: "waste",
    tab: "environment",
    cardKey: "waste",
    displayName: "Waste",
    order: 4,
    icon: Shield,
  },

  // SOCIAL TAB (4 sections)
  {
    id: "employee-support",
    tab: "social",
    cardKey: "employeeSupport",
    displayName: "Employee Support",
    order: 0,
    icon: Users,
  },
  {
    id: "diversity-inclusion",
    tab: "social",
    cardKey: "diversityInclusion",
    displayName: "Diversity & Inclusion",
    order: 1,
    icon: Users2,
  },
  {
    id: "human-rights",
    tab: "social",
    cardKey: "humanRights",
    displayName: "Human Rights",
    order: 2,
    icon: Shield,
  },
  {
    id: "community-contribution",
    tab: "social",
    cardKey: "communityContribution",
    displayName: "Community Contribution",
    order: 3,
    icon: Building2,
  },

  // GOVERNANCE TAB (5 sections)
  {
    id: "ethics",
    tab: "governance",
    cardKey: "ethics",
    displayName: "Ethics",
    order: 0,
    icon: Shield,
  },
  {
    id: "transparency",
    tab: "governance",
    cardKey: "transparency",
    displayName: "Transparency",
    order: 1,
    icon: Eye,
  },
  {
    id: "leadership",
    tab: "governance",
    cardKey: "leadership",
    displayName: "Leadership",
    order: 2,
    icon: Users2,
  },
  {
    id: "risk-management",
    tab: "environment",
    cardKey: "riskManagement",
    displayName: "Climate risk management",
    order: 5,
    icon: AlertOctagon,
  },
  {
    id: "accountability",
    tab: "governance",
    cardKey: "accountability",
    displayName: "Accountability",
    order: 3,
    icon: FileCheck,
  },

  // SUSTAINABILITY STORY TAB (4 sections)
  {
    id: "story-general",
    tab: "sustainability-story",
    cardKey: "storyGeneral",
    displayName: "General",
    order: 0,
    icon: Building2,
  },
  {
    id: "story-environment",
    tab: "sustainability-story",
    cardKey: "storyEnvironment",
    displayName: "Environment",
    order: 1,
    icon: Leaf,
  },
  {
    id: "story-social",
    tab: "sustainability-story",
    cardKey: "storySocial",
    displayName: "Social",
    order: 2,
    icon: Users,
  },
  {
    id: "story-governance",
    tab: "sustainability-story",
    cardKey: "storyGovernance",
    displayName: "Governance",
    order: 3,
    icon: Shield,
  },
];

// Helper functions for section navigation
export function getSectionsByTab(tab: string): WizardSection[] {
  return WIZARD_SECTIONS.filter((s) => s.tab === tab).sort((a, b) => a.order - b.order);
}

export function getSectionById(id: string): WizardSection | undefined {
  return WIZARD_SECTIONS.find((s) => s.id === id);
}

export function getSectionByCardKey(tab: string, cardKey: string): WizardSection | undefined {
  return WIZARD_SECTIONS.find((s) => s.tab === tab && s.cardKey === cardKey);
}

export function getNextSection(currentTab: string, currentSectionId: string): {
  section: WizardSection | null;
  nextTab: string | null;
} {
  const currentSection = getSectionById(currentSectionId);
  if (!currentSection) {
    return { section: null, nextTab: null };
  }

  const tabSections = getSectionsByTab(currentTab);
  const currentIndex = tabSections.findIndex((s) => s.id === currentSectionId);

  // If there's a next section in the current tab
  if (currentIndex < tabSections.length - 1) {
    return { section: tabSections[currentIndex + 1], nextTab: null };
  }

  // Move to next tab
  const tabs = ["general", "environment", "social", "governance", "sustainability-story"];
  const currentTabIndex = tabs.indexOf(currentTab);
  
  if (currentTabIndex < tabs.length - 1) {
    const nextTab = tabs[currentTabIndex + 1];
    const nextTabSections = getSectionsByTab(nextTab);
    return { section: nextTabSections[0] || null, nextTab };
  }

  // No more sections or tabs
  return { section: null, nextTab: null };
}

export function getPreviousSection(currentTab: string, currentSectionId: string): {
  section: WizardSection | null;
  previousTab: string | null;
} {
  const currentSection = getSectionById(currentSectionId);
  if (!currentSection) {
    return { section: null, previousTab: null };
  }

  const tabSections = getSectionsByTab(currentTab);
  const currentIndex = tabSections.findIndex((s) => s.id === currentSectionId);

  // If there's a previous section in the current tab
  if (currentIndex > 0) {
    return { section: tabSections[currentIndex - 1], previousTab: null };
  }

  // Move to previous tab
  const tabs = ["general", "environment", "social", "governance", "sustainability-story"];
  const currentTabIndex = tabs.indexOf(currentTab);
  
  if (currentTabIndex > 0) {
    const previousTab = tabs[currentTabIndex - 1];
    const previousTabSections = getSectionsByTab(previousTab);
    const lastSection = previousTabSections[previousTabSections.length - 1];
    return { section: lastSection || null, previousTab };
  }

  // No previous sections or tabs
  return { section: null, previousTab: null };
}

export function isLastSection(tab: string, sectionId: string): boolean {
  const section = getSectionById(sectionId);
  if (!section || section.tab !== tab) return false;
  
  return tab === "sustainability-story" && section.order === 3; // Last section of last tab
}

export function isFirstSection(tab: string, sectionId: string): boolean {
  const section = getSectionById(sectionId);
  if (!section || section.tab !== tab) return false;
  
  return tab === "general" && section.order === 0; // First section of first tab
}

// Environment tab valid section IDs (after reorganization)
const VALID_ENVIRONMENT_SECTIONS = new Set([
  "carbon-emissions",
  "pollution",
  "biodiversity",
  "water",
  "waste",
  "risk-management"
]);

// Removed Scope 3 sections (now nested under carbon-emissions)
const DEPRECATED_SCOPE3_SECTIONS = new Set([
  "business-travel",
  "employee-commuting",
  "accommodation",
  "company-expenses",
  "freight-transport"
]);

/**
 * Validates if a section ID is a valid top-level Environment section
 * Returns true for the 6 final Environment cards
 * Returns false for deprecated Scope 3 sections that are now nested
 */
export function isValidEnvironmentSection(sectionId: string): boolean {
  return VALID_ENVIRONMENT_SECTIONS.has(sectionId);
}

/**
 * Checks if a section ID is a deprecated Scope 3 section that should redirect
 */
export function isDeprecatedScope3Section(sectionId: string): boolean {
  return DEPRECATED_SCOPE3_SECTIONS.has(sectionId);
}
